import { useState, useEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Edit, Plus, Trash2, ArrowUp, ArrowDown, X } from "lucide-react";
import Sidebar from "./sidebar";
import { useToast } from "../../components/ui/use-toast";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Pagination from "../../components/Pagination";

interface NewsItem {
  id: string;
  title: string;
  content: string;
  images: string[];
  date: string;
}

const NovinkyPage = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState<NewsItem | null>(null);
  const [newsData, setNewsData] = useState({ title: "", content: "" });
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const { toast } = useToast();
  const [selectedNewsId, setSelectedNewsId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const quillRef = useRef<ReactQuill | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const visibleItems = newsItems.slice(startIndex, endIndex)

  useEffect(() => {
    const ROW_HEIGHT = 65
    let PADDING = 220

    const updateRows = () => {
      if (window.innerWidth < 640) {
        PADDING = 350 //Mobile
      }
      const availableHeight = window.innerHeight - PADDING

      const rows = Math.floor(availableHeight / 65) //Row height

      setRowsPerPage(Math.max(1, rows))
    }

    updateRows()
    window.addEventListener("resize", updateRows)
    return () => window.removeEventListener("resize", updateRows)
  }, [])

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['italic', 'underline', 'strike'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }]
    ],
  };

  const formats = [
    "header",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "link",
    "image",
    "align",
    "color",
    "background",
  ];

  useEffect(() => {
    // Customize Quill toolbar labels and add custom styles
    const customizeQuill = () => {
      const toolbar = document.querySelector('.ql-toolbar');
      if (!toolbar) return;

      // Remove default header labels
      const headerPicker = toolbar.querySelector('.ql-header .ql-picker-label');
      const headerItems = toolbar.querySelectorAll('.ql-header .ql-picker-item');

      // Remove any existing custom styles
      const existingStyle = document.querySelector('style[data-quill-style]');
      if (existingStyle) existingStyle.remove();

      // Add new styles
      const style = document.createElement('style');
      style.setAttribute('data-quill-style', 'true');
      style.textContent = `
        .ql-editor {
          font-family: inherit;
        }
        .ql-editor h1,
        .ql-editor h2,
        .ql-editor h3 {
          text-align: inherit;
          margin: 0;
          padding: 0;
        }
        .ql-editor em, 
        .ql-editor i {
          font-style: italic !important;
        }
        .ql-editor .ql-align-center {
          text-align: center;
        }
        .ql-editor .ql-align-right {
          text-align: right;
        }
        .ql-editor .ql-align-justify {
          text-align: justify;
        }
      `;
      document.head.appendChild(style);
    };

    // Run customization after a small delay to ensure Quill is initialized
    const timer = setTimeout(customizeQuill, 100);
    return () => {
      clearTimeout(timer);
      const style = document.querySelector('style[data-quill-style]');
      if (style) style.remove();
    };
  }, [isCreateDialogOpen, isEditDialogOpen]);

  const fetchNews = async () => {
    try {
      const response = await fetch("https://preview.zushm.cz/api/novinky/");
      const data = await response.json();

      const formattedNews = data
        .filter((item: any) => item.DeletedAt === null)
        .map((item: any) => ({
          id: item.ID.toString(),
          title: item.Title,
          content: item.Content,
          images: item.Image ? item.Image.split(",") : [],
          date: item.CreatedAt.split("T")[0],
        }));

      setNewsItems(formattedNews);
    } catch (error) {
      console.error("Error fetching news data:", error);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleFileUpload = async (files: FileList) => {
    const token = document.cookie.split("; ").find(row => row.startsWith("token"));
    if (!token) {
      toast({
        title: "Chyba",
        description: "Nepodařilo se ověřit vaše oprávnění",
        variant: "destructive",
      });
      return;
    }

    const tokenValue = token.split("=")[1];
    const formData = new FormData();
    
    Array.from(files).forEach((file) => {
      formData.append(`files`, file);
    });

    try {
      const response = await fetch("https://preview.zushm.cz/api/novinky/upload", {
        method: "POST",
        headers: {
          "Authorization": tokenValue,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const data = await response.json();
      const quill = quillRef.current?.getEditor();
      
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          data.files.forEach((file: { url: string, name: string }) => {
            quill.insertEmbed(range.index, 'link', {
              file: true,
              href: file.url,
              name: file.name
            }, 'user');
            quill.setSelection(range.index + 1, 0, 'silent');
          });
        }
      }

      toast({
        title: "Úspěch",
        description: "Soubory byly úspěšně nahrány.",
      });
    } catch (error) {
      console.error("Error uploading files:", error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se nahrát soubory",
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
    }
    e.target.value = ''; // Reset input
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const files = Array.from(event.target.files).slice(0, 10 - selectedImages.length);
    setSelectedImages([...selectedImages, ...files]);

    const newImagePreviewUrls: string[] = [];
    files.forEach((file) => {
      newImagePreviewUrls.push(URL.createObjectURL(file));
    });
    setImagePreviewUrls([...imagePreviewUrls, ...newImagePreviewUrls]);
  };

  const handleCreateNews = async () => {
    if (!newsData.title || !newsData.content) {
      toast({
        title: "Chyba",
        description: "Prosím vyplňte titulek a obsah novinky.",
        variant: "destructive",
      });
      return;
    }
    setSelectedImages([]);
    setImagePreviewUrls([]);

    const token = document.cookie.split("; ").find(row => row.startsWith("token"));

    if (token) {
      const tokenValue = token.split("=")[1];
      const formData = new FormData();
      formData.append("title", newsData.title);
      formData.append("content", newsData.content);

      selectedImages.forEach((file, index) => {
        formData.append(`image${index}`, file);
      });

      try {
        const response = await fetch("https://preview.zushm.cz/api/novinky/create", {
          method: "POST",
          headers: {
            "Authorization": tokenValue,
          },
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json();
          toast({
            title: "Chyba",
            description: data.message || "Nepodařilo se vytvořit novinku",
            variant: "destructive",
          });
          return;
        }

        const data = await response.json();
        toast({
          title: "Úspěch",
          description: "Novinka byla úspěšně vytvořena.",
        });
        fetchNews();
      } catch (error) {
        console.error("Error uploading data:", error);
        toast({
          title: "Chyba",
          description: "Nastala chyba při vytváření novinky",
          variant: "destructive",
        });
      }
    } else {
      console.error("No token found");
      toast({
        title: "Chyba",
        description: "Nepodařilo se ověřit vaše oprávnění",
        variant: "destructive",
      });
    }

    setNewsData({ title: "", content: "" });
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setIsCreateDialogOpen(false);
  };

  const handleEditNews = async () => {
    if (!currentNews || !newsData.title || !newsData.content) {
      toast({
        title: "Chyba",
        description: "Prosím vyplňte titulek a obsah novinky.",
        variant: "destructive",
      });
      return;
    }

    const token = document.cookie.split("; ").find(row => row.startsWith("token"));

    if (token) {
      const tokenValue = token.split("=")[1];
      try {
        const response = await fetch("https://preview.zushm.cz/api/novinky/update", {
          method: "POST",
          headers: {
            "Authorization": tokenValue,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: parseInt(currentNews.id),
            title: newsData.title,
            content: newsData.content,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          toast({
            title: "Úspěch",
            description: "Novinka byla úspěšně upravena.",
          });
          setNewsItems((prev) => prev.map((item) =>
            item.id === currentNews.id ? { ...item, ...newsData } : item
          ));
        } else {
          toast({
            title: "Neúspěch",
            description: "Novinka nebyla úspěšně upravena.",
          });
        }
      } catch (error) {
        console.error("Error updating data:", error);
        toast({
          title: "Neúspěch",
          description: "Novinka nebyla úspěšně upravena.",
        });
      }
    } else {
      console.error("No token found");
      toast({
        title: "Neúspěch",
        description: "Novinka nebyla úspěšně upravena.",
      });
    }

    setNewsData({ title: "", content: "" });
    setIsEditDialogOpen(false);
    setSelectedImages([]);
    setImagePreviewUrls([]);
    fetchNews();
  };

  const handleDeleteNews = (id: string) => {
    const token = document.cookie.split("; ").find(row => row.startsWith("token="));

    if (token) {
      const tokenValue = token.split("=")[1];

      fetch("https://preview.zushm.cz/api/novinky/delete", {
        method: "POST",
        headers: {
          "Authorization": tokenValue,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: id,
        }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.message === "Novinka deleted") {
            toast({
              title: "Úspěch",
              description: "Novinka byla úspěšně smazána.",
            });
            fetchNews();
          } else {
            console.error("Failed to delete novinka:", data.message);
            toast({
              title: "Neúspěch",
              description: "Novinka nebyla úspěšně smazána.",
            });
          }
        })
        .catch(error => {
          console.error("Error deleting novinka", error);
          toast({
            title: "Neúspěch",
            description: "Novinka nebyla úspěšně smazána.",
          });
        });
    } else {
      console.error("No token found");
      toast({
        title: "Neúspěch",
        description: "Novinka nebyla úspěšně smazána.",
      });
    }
  };

  const openEditDialog = (news: NewsItem) => {
    setCurrentNews(news);
    setNewsData({ title: news.title, content: news.content });
    setImagePreviewUrls([]);
    setSelectedImages([]);
    setIsEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setCurrentNews(null);
    setNewsData({ title: "", content: "" });
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setIsCreateDialogOpen(true);
  };

  const confirmDelete = (id: string) => {
    setSelectedNewsId(id);
    setIsDeleteDialogOpen(true);
  };

  const executeDelete = () => {
    if (selectedNewsId) {
      handleDeleteNews(selectedNewsId);
      setIsDeleteDialogOpen(false);
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...selectedImages];
    newImages.splice(index, 1);
    setSelectedImages(newImages);

    const newPreviews = [...imagePreviewUrls];
    URL.revokeObjectURL(newPreviews[index]);
    newPreviews.splice(index, 1);
    setImagePreviewUrls(newPreviews);
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    const newImages = [...selectedImages];
    const newPreviews = [...imagePreviewUrls];
    
    if (direction === 'up' && index > 0) {
      [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
      [newPreviews[index], newPreviews[index - 1]] = [newPreviews[index - 1], newPreviews[index]];
    } else if (direction === 'down' && index < newImages.length - 1) {
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      [newPreviews[index], newPreviews[index + 1]] = [newPreviews[index + 1], newPreviews[index]];
    }
    
    setSelectedImages(newImages);
    setImagePreviewUrls(newPreviews);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newImages = [...selectedImages];
    const newPreviews = [...imagePreviewUrls];
    
    const draggedItem = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);
    
    const draggedPreview = newPreviews[draggedIndex];
    newPreviews.splice(draggedIndex, 1);
    newPreviews.splice(index, 0, draggedPreview);
    
    setSelectedImages(newImages);
    setImagePreviewUrls(newPreviews);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 pt-20 sm:p-6">
        <div className="space-y-6 w-screen sm:w-full">
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4">
            <h1 className="text-2xl font-bold w-full text-center sm:text-left">Novinky</h1>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Přidat novinku
            </Button>
          </div>


          {/* News List */}
          <Table className="w-full min-w-[0]">
            <TableHeader>
              <TableRow>
                <TableHead className="sm:min-w-[200px]">Titulek</TableHead>
                <TableHead className="min-w-[100px] hidden md:table-cell">Datum</TableHead>
                <TableHead className="min-w-[120px] hidden md:table-cell">Obrázky</TableHead>
                <TableHead className="min-w-[150px]">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {newsItems
                .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                .map((news) => (
                  <TableRow key={news.id}>
                    <TableCell className="font-medium">{news.title}</TableCell>
                    <TableCell className="hidden md:table-cell">{news.date}</TableCell>
                    <TableCell className="hidden md:table-cell">{(news.images || []).length} obrázků</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(news)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Upravit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDelete(news.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Pagination totalItems={newsItems.length} rowsPerPage={rowsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />

          {/* Create News Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
              <DialogHeader className="px-6">
                <DialogTitle>Přidat novinku</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-4 px-6">
                <div>
                  <Label htmlFor="title">Titulek</Label>
                  <Input
                    id="title"
                    value={newsData.title}
                    onChange={(e) => setNewsData({ ...newsData, title: e.target.value })}
                    placeholder="Zadejte titulek"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Obsah</Label>
                  <div className="rounded-md border border-input">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={newsData.content}
                      onChange={(content) => setNewsData({ ...newsData, content })}
                      modules={modules}
                      formats={formats}
                      placeholder="Zadejte obsah novinky..."
                      className="h-64 pb-10"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <Label>Obrázky (max 10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Plus className="w-8 h-8 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">Přetáhněte obrázky sem nebo klikněte pro výběr</p>
                        <p className="text-xs text-gray-500">
                          {selectedImages.length}/10 obrázků
                        </p>
                      </div>
                      <input
                        id="images"
                        type="file"
                        className="hidden"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={selectedImages.length >= 10}
                      />
                    </label>
                  </div>
                  {imagePreviewUrls.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium mb-3">Náhled obrázků (můžete změnit pořadí):</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {imagePreviewUrls.map((url, index) => (
                          <div 
                            key={index}
                            className="relative group border rounded-lg p-3 bg-gray-50"
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="flex gap-2 mb-2">
                              <button
                                type="button"
                                onClick={() => moveImage(index, 'up')}
                                disabled={index === 0}
                                className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-200'}`}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveImage(index, 'down')}
                                disabled={index === imagePreviewUrls.length - 1}
                                className={`p-1 rounded ${index === imagePreviewUrls.length - 1 ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-200'}`}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="p-1 rounded text-gray-700 hover:bg-gray-200 ml-auto"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="aspect-video overflow-hidden rounded-lg bg-white">
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="object-cover w-full h-full"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-4 px-6 py-4">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Zrušit
                </Button>
                <Button onClick={handleCreateNews}>Vytvořit novinku</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit News Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
              <DialogHeader className="px-6">
                <DialogTitle>Upravit novinku</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-4 px-6">
                <div>
                  <Label htmlFor="edit-title">Titulek</Label>
                  <Input
                    id="edit-title"
                    value={newsData.title}
                    onChange={(e) => setNewsData({ ...newsData, title: e.target.value })}
                    placeholder="Zadejte titulek"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-content">Obsah</Label>
                  <div className="rounded-md border border-input">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={newsData.content}
                      onChange={(content) => setNewsData({ ...newsData, content })}
                      modules={modules}
                      formats={formats}
                      placeholder="Zadejte obsah novinky..."
                      className="h-64 pb-10"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <Label>Obrázky</Label>
                  {currentNews?.images?.length ? (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium mb-3">Náhled obrázků:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {currentNews.images.map((image, index) => (
                          <div 
                            key={index}
                            className="relative border rounded-lg p-3 bg-gray-50"
                          >
                            <div className="aspect-video overflow-hidden rounded-lg bg-white">
                              <img
                                src={image}
                                alt={`Preview ${index + 1}`}
                                className="object-cover w-full h-full"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg bg-gray-50 mt-2">
                      <p className="text-gray-500">Žádné obrázky</p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-4 px-6 py-4">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Zrušit
                </Button>
                <Button onClick={handleEditNews}>Uložit změny</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete News Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Smazat novinku</DialogTitle>
                <DialogDescription>Opravdu chcete smazat tuto novinku? Tato akce je nevratná.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="destructive" onClick={executeDelete}>
                  Smazat
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                  Zrušit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default NovinkyPage;